import React, { ReactElement } from 'react';
import { Select, Checkbox } from '@/components/ui';

interface Option {
  label: string;
  value: string;
  color?: string;
  initials?: string;
  /** Secondary line under the label (e.g. email), also matched when searching */
  description?: string;
}

interface CustomMultiSelectProps {
  options: Option[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  colorPalette?: string[];
  dropdownRender?: (menu: ReactElement) => ReactElement;
}

const CustomMultiSelect: React.FC<CustomMultiSelectProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder = "Select options",
  colorPalette = ['#EB1964', '#F26B3A', '#3AA5F2', '#FFC299'],
  dropdownRender
}) => {
  // Two-line options need a taller row or the virtual list mis-measures scrolling
  const hasDescriptions = options.some(option => Boolean(option.description));

  return (
    <Select
      mode="multiple"
      placeholder={placeholder}
      loading={false}
      value={selectedValues}
      onChange={onChange}
      showSearch
      size="small"
      className="w-full"
      filterOption={(input: string, option: any) => {
        const needle = input.toLowerCase();
        return (
          option.label.toLowerCase().includes(needle) ||
          (option.description || '').toLowerCase().includes(needle)
        );
      }}
      allowClear
      style={{ width: '100%' }}
      menuItemSelectedIcon={null} // Remove default selected icon
      dropdownStyle={{ padding: '4px' }}
      listHeight={hasDescriptions ? 260 : 180}
      listItemHeight={hasDescriptions ? 44 : undefined}
      maxTagCount={3}
      maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
      showArrow={true}
      dropdownRender={dropdownRender}
      removeIcon={<span className="text-gray-500 hover:text-gray-700">×</span>}
      tagRender={(props) => {
        const { label, closable, onClose } = props;
        return (
          <div className="bg-gray-100 rounded px-2 py-1 mr-1 mb-1 flex items-center text-xs">
            <span className="mr-1">{label}</span>
            {closable && (
              <span 
                className="cursor-pointer text-gray-500 hover:text-gray-700"
                onClick={onClose}
              >
                ×
              </span>
            )}
          </div>
        );
      }}
      optionRender={(option) => {
        const optionData = options.find(o => o.value === option.value);
        if (!optionData) return null;

        const isSelected = selectedValues.includes(optionData.value);
        const colorIndex = options.findIndex(o => o.value === optionData.value) % colorPalette.length;
        const backgroundColor = optionData.color || colorPalette[colorIndex];
        const initials = optionData.initials || optionData.label.charAt(0).toUpperCase();

        return (
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center min-w-0">
              <div
                className="w-5 h-5 rounded-full text-white flex items-center justify-center text-[10px] mr-2 flex-shrink-0"
                style={{ backgroundColor }}
              >
                {initials}
              </div>
              <div className="min-w-0">
                <div className="text-sm truncate">{optionData.label}</div>
                {/* Names repeat across accounts, so the email is what identifies the person */}
                {optionData.description && (
                  <div className="text-xs text-black/45 truncate">{optionData.description}</div>
                )}
              </div>
            </div>
            <Checkbox checked={isSelected} size="small" />
          </div>
        );
      }}
      options={options}
    />
  );
};

export default CustomMultiSelect;
